import useSimBilling from 'api/sims/useSimBilling';
import useSimDetails from 'api/sims/useSimDetails';
import useSimStatuses from 'api/sims/useSimStatuses';
import Field, { FieldProps } from 'components/Field/Field';
import useUserInfo from 'hooks/User/useUserInfo';
import { type FC } from 'react';
import type { EnumsSimNetworkState } from 'types/__generated__/SimInventoryApi';
import getUserFriendlyBytes from 'utils/bytes/getUserFriendlyBytes';
import styles from './IncludedDataField.module.css';

interface IncludedDataFieldProps {
  hologramSimId?: HologramSimId;
  showPauseInfo?: boolean;
  direction?: FieldProps['direction'];
}

const IncludedDataField: FC<IncludedDataFieldProps> = ({
  hologramSimId,
  showPauseInfo = true,
  direction = 'column',
}) => {
  const {
    simDetails,
    error: simDetailsError,
    isLoading: simDetailsIsLoading,
  } = useSimDetails(hologramSimId);

  const {
    simBilling,
    error: simBillingError,
    isLoading: simBillingIsLoading,
  } = useSimBilling(hologramSimId);

  const {
    networkStatusGroups,
    isLoading: isStatusesLoading,
    error: statusesError,
  } = useSimStatuses();

  const error = simBillingError || simDetailsError || statusesError;
  const isLoading = simBillingIsLoading || simDetailsIsLoading || isStatusesLoading;

  const { networkStatus } = simDetails;
  const { organizationBillingMethod, dataIncluded, dataUsageLimit, planType, poolDataLimit } =
    simBilling;

  const isPredeployment =
    !networkStatus ||
    !!networkStatusGroups?.predeployment?.includes(networkStatus as EnumsSimNetworkState);

  const isPrepaid = organizationBillingMethod === 'prepaid';
  const isPostpaid = organizationBillingMethod === 'postpaid';

  const isPayAsYouGo = (dataIncluded ?? 0) === 0 && !poolDataLimit;

  const { organizationRole } = useUserInfo();

  if (
    isLoading ||
    error ||
    isPredeployment ||
    !['admin', 'manager', 'editor'].includes(organizationRole)
  ) {
    return null;
  }

  return (
    <Field
      label="Included data"
      value={
        <>
          {poolDataLimit && `${getUserFriendlyBytes(poolDataLimit, 'short')} data pool `}
          {isPayAsYouGo && planType}
          {!isPayAsYouGo && getUserFriendlyBytes(dataIncluded, 'short')}{' '}
          {showPauseInfo && (
            <span className={styles.diminished}>
              {isPrepaid &&
                typeof dataUsageLimit !== 'undefined' &&
                dataUsageLimit > 0 &&
                `(pause at ${getUserFriendlyBytes(dataUsageLimit, 'short')})`}
              {(!dataUsageLimit || dataUsageLimit === 0) && !isPostpaid && `(no limit)`}
            </span>
          )}
        </>
      }
      direction={direction}
    />
  );
};

export default IncludedDataField;
